import { useState } from 'react'
import firebase from '../../firebase/compat'
import TwoFactorStatusBadge from '../auth/TwoFactorStatusBadge'

// Tweestapsverificatie herstellen voor iemand die zijn telefoon kwijt is (MFA
// fase 6). Alleen in de Deccos-beheertenant getoond; de echte poort
// (`Users.SuperAdmin`), de verplichte reden en het weigeren van het eigen
// account staan op de server — deze pagina toont wat terugkomt.
// AI-CONTEXT: See docs/features/mfa-totp.md
// AI-CONTEXT: See docs/patterns.md#cf-superadmin-cross-tenant-write

/**
 * @typedef {object} FactorSummary
 * @property {string} factorId - 'totp' of 'phone'
 * @property {string} displayName - Naam bij het koppelen
 * @property {string} enrollmentTime - Moment van koppelen
 */

/**
 * @typedef {object} MfaStatus
 * @property {boolean} found
 * @property {string} [uid]
 * @property {string} [email]
 * @property {string} [displayName]
 * @property {Array<FactorSummary>} [factors]
 */

/** @param {string} name @returns {Function} callable in europe-west1 */
const callable = (name) => firebase.app().functions('europe-west1').httpsCallable(name)

/** @param {FactorSummary} f @returns {string} */
const factorLabel = (f) => {
    const naam = f.factorId === 'totp' ? 'Authenticator-app' : f.factorId === 'phone' ? 'Sms' : f.factorId
    const datum = f.enrollmentTime ? new Date(f.enrollmentTime).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
    return datum ? `${naam}, gekoppeld op ${datum}` : naam
}

/**
 * @param {{status: string, signedOut?: boolean}} result - Teruggave van resetUserMfa
 * @param {string} email
 * @returns {string}
 */
const resetMessage = (result, email) => {
    if (result.status === 'nothing-to-reset') return `${email} had al geen tweede stap; er is niets verwijderd.`
    if (result.signedOut === false) return `De tweede stap is verwijderd, maar uitloggen op andere apparaten is mislukt. ${email} kan weer inloggen met alleen het wachtwoord.`
    return `De tweede stap is verwijderd. ${email} is overal uitgelogd en kan weer inloggen met alleen het wachtwoord.`
}

/**
 * Geen props: werkt op de gebruiker die je opzoekt.
 */
const MfaRecovery = () => {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState(/** @type {MfaStatus | null} */ (null))
    const [reason, setReason] = useState('')
    const [confirm, setConfirm] = useState(false)
    const [busy, setBusy] = useState(false)
    const [message, setMessage] = useState('')

    /** @param {string} adres */
    const lookup = async (adres) => {
        const result = await callable('getUserMfaStatus')({ email: adres })
        setStatus(result.data)
    }

    const search = async () => {
        setBusy(true)
        setMessage('')
        setConfirm(false)
        try {
            await lookup(email)
        } catch (e) {
            setStatus(null)
            setMessage(`Opzoeken is mislukt: ${e.message}`)
        } finally {
            setBusy(false)
        }
    }

    const reset = async () => {
        setBusy(true)
        setMessage('')
        let result
        try {
            result = (await callable('resetUserMfa')({ uid: status.uid, reason })).data
        } catch (e) {
            setMessage(e.message)
            setBusy(false)
            return
        }
        setConfirm(false)
        setReason('')
        setMessage(resetMessage(result, status.email))
        // Alleen om de badge bij te werken. Faalt dit, dan blijft de melding
        // hierboven staan: het herstel zelf is gelukt.
        try {
            await lookup(status.email)
        } catch (e) {
            console.error('Opnieuw opzoeken na herstel mislukt:', e)
        } finally {
            setBusy(false)
        }
    }

    const factors = status?.factors || []

    return (
        <div className='dashboard-container two-factor-settings'>
            <h2>Tweestapsverificatie herstellen</h2>
            <p>Voor iemand die zijn telefoon kwijt is. Controleer eerst of je echt met die persoon praat.</p>

            <label htmlFor='mfa-recovery-email'>E-mailadres van de gebruiker</label>
            <input
                id='mfa-recovery-email'
                className='input-classic'
                type='email'
                value={email}
                onChange={(e) => { const value = e.target.value; setEmail(value) }}
                onKeyDown={(e) => { if (e.key === 'Enter') search() }}
            />
            <div className='two-factor-actions'>
                <button onClick={search} disabled={busy}>Opzoeken</button>
            </div>

            {status && !status.found && <p>Geen account met dit e-mailadres.</p>}

            {status?.found && (
                <>
                    <div className='two-factor-header'>
                        <h3>{status.displayName || status.email}</h3>
                        <TwoFactorStatusBadge on={factors.length > 0} />
                    </div>
                    {status.displayName && <p>{status.email}</p>}
                    {factors.map((f) => <p key={`${f.factorId}-${f.enrollmentTime}`}>{factorLabel(f)}</p>)}

                    {factors.length > 0 && (
                        <>
                            <label htmlFor='mfa-recovery-reason'>Hoe heb je de identiteit gecontroleerd?</label>
                            <input
                                id='mfa-recovery-reason'
                                className='input-classic'
                                type='text'
                                value={reason}
                                onChange={(e) => { const value = e.target.value; setReason(value) }}
                            />
                            <div className='two-factor-actions'>
                                {confirm ? (
                                    <>
                                        <button onClick={reset} disabled={busy}>Ja, verwijderen</button>
                                        <button onClick={() => setConfirm(false)} disabled={busy}>Annuleren</button>
                                    </>
                                ) : (
                                    <button onClick={() => setConfirm(true)} disabled={busy}>Tweede stap verwijderen</button>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}

            {message && <p role='alert'>{message}</p>}
        </div>
    )
}

export default MfaRecovery
